import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";
import api from "@/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { pickFirstSessionPerBlock, formatDateShortUK } from "./_helpers";

export default function QAAdmin() {
  const [sessions, setSessions] = useState([]);
  const [featuredId, setFeaturedId] = useState("");
  const [sid, setSid] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/sessions"), api.get("/info")])
      .then(([s, info]) => {
        setSessions(s.data);
        const fid = info.data?.featured_qa_session_id || "";
        setFeaturedId(fid);
        // Auto-select the featured session if one is set - delegates can only
        // submit Qs there, so this is the only block that ever has questions.
        if (fid) setSid(fid);
      });
  }, []);

  useEffect(() => { if (sid) api.get(`/sessions/${sid}/questions`).then((r) => setRows(r.data)); }, [sid]);
  const moderate = async (q, status) => {
    await api.patch(`/questions/${q.id}`, { status });
    setRows((prev) => prev.map((x) => x.id === q.id ? { ...x, status } : x));
  };

  const blocks = useMemo(() => pickFirstSessionPerBlock(sessions), [sessions]);
  const featuredSession = useMemo(
    () => sessions.find((s) => s.id === featuredId),
    [sessions, featuredId]
  );

  return (
    <div>
      {featuredId && featuredSession ? (
        <div className="p-3 bg-[#E6F8FC] border border-[#43C4E3] rounded-sm mb-3 text-sm" data-testid="admin-qa-featured-banner">
          Moderating questions for the featured Q&amp;A session:
          <div className="font-display text-[#1c1c24] mt-0.5">
            {formatDateShortUK(featuredSession.date)} {featuredSession.start_time} · {featuredSession.stage_label || featuredSession.stage} · {featuredSession.title}
          </div>
        </div>
      ) : (
        <Select value={sid} onValueChange={setSid}>
          <SelectTrigger className="rounded-sm mb-3" data-testid="admin-qa-session-select">
            <SelectValue placeholder="Select session block…" />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            {blocks.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {formatDateShortUK(s.date)} {s.start_time} · {s.stage_label || s.stage} · {s.track || s.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <div className="space-y-2">
        {rows.map((q) => (
          <div key={q.id} className="p-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm" data-testid={`admin-qa-${q.id}`}>
            <p className="text-sm">{q.text}</p>
            <div className="text-xs text-[#7A828C] mt-1 mb-2">{q.user_name} · {q.upvote_count} upvotes · status: {q.status}</div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => moderate(q, "answered")} className="rounded-sm"><CheckCircle size={14} className="mr-1" /> Mark answered</Button>
              <Button size="sm" variant="outline" onClick={() => moderate(q, "hidden")} className="rounded-sm"><EyeSlash size={14} className="mr-1" /> Hide</Button>
              <Button size="sm" variant="outline" onClick={() => moderate(q, "approved")} className="rounded-sm"><Eye size={14} className="mr-1" /> Unhide</Button>
            </div>
          </div>
        ))}
        {sid && rows.length === 0 && <div className="text-[#7A828C] text-center py-6 text-sm">No questions yet.</div>}
        {!sid && !featuredId && <div className="text-[#7A828C] text-center py-6 text-sm">Pick a Featured Q&amp;A session in Admin → Info to restrict submissions to one panel.</div>}
      </div>
    </div>
  );
}
